/**
 * Dev-only authentication bypass.
 *
 * When REACT_APP_DEV_BYPASS_AUTH=true and NODE_ENV=development, the app
 * automatically signs in via the server's /api/auth/dev-login endpoint,
 * which sets an httpOnly session cookie and returns non-sensitive user info.
 *
 * Both guards must be true for any bypass behaviour to activate:
 *   1. process.env.NODE_ENV === 'development'  (enforced at build time by CRA)
 *   2. process.env.REACT_APP_DEV_BYPASS_AUTH === 'true'  (set in .env.development)
 *
 * The server endpoint also returns 404 in production, so even if a bad build
 * somehow included this code with the flag set, the server would reject it.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/** User info returned by the dev-login endpoint (no JWT — it's in the httpOnly cookie). */
export interface DevAuthResult {
    userId: string;
    name: string;
    linkedinId: string;
}

/**
 * Returns true only when the dev bypass is explicitly enabled.
 * This is deliberately verbose so it is easy to audit.
 */
export function isDevBypassEnabled(): boolean {
    return (
        process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_DEV_BYPASS_AUTH === 'true'
    );
}

/**
 * Calls the server-side dev-only login endpoint.
 * The server sets an httpOnly cookie and returns non-sensitive user metadata.
 * The server rejects this call with 404 when NODE_ENV=production.
 * Throws on any non-OK response.
 */
export async function fetchDevAuth(): Promise<DevAuthResult> {
    // credentials: 'include' is required so the server's Set-Cookie header is accepted.
    const resp = await fetch(`${API_BASE}/api/auth/dev-login`, {
        method: 'POST',
        credentials: 'include',
    });
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || `Dev login failed: HTTP ${resp.status}`);
    }
    return resp.json() as Promise<DevAuthResult>;
}
